import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconLink, IconTrash } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { notifications } from '@mantine/notifications';
import {
  useAttachContractParty,
  useContractPartiesList,
  useDetachContractParty,
} from '../../queries/contracts';
import { useSearchParties } from '../../queries/parties';
import { ApiError } from '../../lib/api-error';
import type { ContractParty, PartyRole } from '../../types/api';

interface Props {
  contractId: string;
  canWrite: boolean;
}

const ROLE_OPTIONS: Array<{ value: PartyRole; label: string }> = [
  { value: 'grantor', label: 'Grantor (landowner)' },
  { value: 'grantee', label: 'Grantee (operator)' },
  { value: 'other', label: 'Other / witness' },
];

export function ContractPartiesPanel({ contractId, canWrite }: Props) {
  const { data: rows = [], isLoading } = useContractPartiesList(contractId);
  const attachM = useAttachContractParty();
  const detachM = useDetachContractParty();

  const [search, setSearch] = useState('');
  const [selectedParty, setSelectedParty] = useState<string | null>(null);
  const [role, setRole] = useState<PartyRole>('grantor');
  const [note, setNote] = useState('');
  const { data: searchResults = [] } = useSearchParties(search);

  const partyOptions = searchResults.map((p) => ({
    value: p.id,
    label: p.canonical_name,
  }));

  async function onAttach() {
    if (!selectedParty) return;
    try {
      await attachM.mutateAsync({
        contract_id: contractId,
        party_id: selectedParty,
        role,
        note: note.trim() || null,
        party_order: rows.filter((r) => r.role === role).length,
      });
      setSelectedParty(null);
      setSearch('');
      setNote('');
      notifications.show({ title: 'Party attached', message: '', color: 'pine' });
    } catch (err) {
      notifications.show({
        title: 'Attach failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  async function onDetach(row: ContractParty) {
    try {
      await detachM.mutateAsync(row.id);
      notifications.show({
        title: 'Party detached',
        message: row.party_canonical_name ?? '',
        color: 'pine',
      });
    } catch (err) {
      notifications.show({
        title: 'Detach failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4}>Parties</Title>
        <Badge variant="light" color="indigo">
          {rows.length} attached
        </Badge>
      </Group>

      {canWrite && (
        <Group align="flex-end" wrap="wrap">
          <Select
            label="Find a party"
            placeholder="Type to search"
            data={partyOptions}
            value={selectedParty}
            onChange={setSelectedParty}
            searchable
            searchValue={search}
            onSearchChange={setSearch}
            clearable
            w={280}
          />
          <Select
            label="Role"
            data={ROLE_OPTIONS}
            value={role}
            onChange={(v) => setRole((v as PartyRole) ?? 'grantor')}
            w={200}
          />
          <TextInput
            label="Note"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            w={240}
          />
          <Button
            leftSection={<IconLink size={16} />}
            onClick={onAttach}
            disabled={!selectedParty}
            loading={attachM.isPending}
          >
            Attach
          </Button>
        </Group>
      )}

      <DataTable
        withTableBorder
        minHeight={120}
        fetching={isLoading}
        records={rows}
        idAccessor="id"
        noRecordsText="No parties attached yet"
        columns={[
          {
            accessor: 'party_canonical_name',
            title: 'Party',
            render: (r) => (
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {r.party_canonical_name ?? r.party_id}
                </Text>
                {r.party_type && (
                  <Text size="xs" c="dimmed">
                    {r.party_type}
                  </Text>
                )}
              </Stack>
            ),
          },
          {
            accessor: 'role',
            title: 'Role',
            width: 140,
            render: (r) => (
              <Badge
                variant="light"
                color={r.role === 'grantor' ? 'pine' : r.role === 'grantee' ? 'indigo' : 'gray'}
              >
                {r.role}
              </Badge>
            ),
          },
          { accessor: 'note', title: 'Note', ellipsis: true },
          {
            accessor: 'actions',
            title: '',
            width: 50,
            render: (r) =>
              canWrite ? (
                <Tooltip label="Detach">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    onClick={() => onDetach(r)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              ) : null,
          },
        ]}
      />
    </Stack>
  );
}
